import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { withWalletTurn, publishClaimWallet } from './walletTurns';

const api = `${process.env.REACT_APP_BACKEND_URL}/api/account/daily-credits`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('gv_token')}` });

export const useDailyCredits = (owner) => {
  const [snapshot, setSnapshot] = useState(null), [now, setNow] = useState(performance.now());
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  const controller = useRef(null), claiming = useRef(false), sequence = useRef(0);
  const apply = useCallback((data) => { const receivedAt = performance.now(); setSnapshot({ data, receivedAt }); setNow(receivedAt); }, []);
  const load = useCallback(async () => {
    if (claiming.current || !controller.current || controller.current.signal.aborted) return;
    const active = controller.current, request = ++sequence.current;
    try {
      const { data } = await axios.get(api, { headers: headers(), signal: active.signal });
      if (!active.signal.aborted && request === sequence.current) { apply(data); setError(''); }
    } catch (failure) { if (!axios.isCancel(failure) && !active.signal.aborted && request === sequence.current) setError('Unable to load daily reward. Please retry.'); }
  }, [apply]);
  useEffect(() => {
    controller.current = new AbortController(); load();
    const tick = setInterval(() => setNow(performance.now()), 1000);
    const poll = setInterval(() => { if (!document.hidden) load(); }, 10000);
    const storage = (event) => { if (event.key === `gv_daily_claim_${owner}`) load(); };
    window.addEventListener('focus', load); window.addEventListener('storage', storage); window.addEventListener('gv-wallet-updated', load);
    window.addEventListener('gv-daily-settings-updated', load);
    return () => { controller.current.abort(); clearInterval(tick); clearInterval(poll); window.removeEventListener('focus', load); window.removeEventListener('storage', storage); window.removeEventListener('gv-wallet-updated', load); window.removeEventListener('gv-daily-settings-updated', load); };
  }, [load, owner]);
  const remaining = snapshot ? Math.max(0, Math.ceil(snapshot.data.remaining_seconds - (now - snapshot.receivedAt) / 1000)) : 0;
  useEffect(() => { if (snapshot && !snapshot.data.can_claim && !snapshot.data.blocked_reason && remaining === 0) load(); }, [remaining, snapshot, load]);
  const syncWallet = () => {
    window.dispatchEvent(new CustomEvent('gv-wallet-updated'));
    try { localStorage.setItem(`gv_daily_claim_${owner}`, String(Date.now())); } catch { /* Focus/poll still synchronize other tabs. */ }
  };
  const claim = async () => {
    if (claiming.current || !snapshot?.data.can_claim) return false;
    claiming.current = true; sequence.current += 1; setBusy(true); setError('');
    const active = controller.current;
    try {
      const { data } = await withWalletTurn(owner, async () => {
        if (active.signal.aborted) return { data: null };
        try {
          const response = await axios.post(`${api}/claim`, {}, { headers: headers(), signal: active.signal });
          publishClaimWallet(owner, response.data); return response;
        } catch (failure) {
          if (!active.signal.aborted) {
            try { const balance = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/keno/wallet`, { headers: headers(), signal: active.signal }); publishClaimWallet(owner, balance.data); } catch { /* Poll/focus retries reconciling offline balances. */ }
          }
          throw failure;
        }
      });
      if (!active.signal.aborted) {
        apply(data); syncWallet();
        const receipt = { user_id: owner, kind: 'daily', request_id: `daily-${data.next_claim_at || data.revision}`, amount_minor: data.reward_minor };
        window.dispatchEvent(new CustomEvent('gv-reward-claimed', { detail: receipt }));
        return receipt;
      }
    } catch (failure) {
      if (axios.isCancel(failure) || active.signal.aborted) return;
      const detail = failure.response?.data?.detail;
      if (detail?.status) { apply(detail.status); syncWallet(); }
      if (failure.response?.status !== 409) setError(typeof detail === 'string' ? detail : detail?.message || 'Unable to claim. Please try again.');
    } finally { if (!active.signal.aborted) { claiming.current = false; setBusy(false); } }
    return false;
  };
  return { data: snapshot?.data, remaining, busy, error, claim, reload: load };
};